import { Formik } from 'formik';
import { useCallback } from 'react';
import { object, number, string } from 'yup';

import type { SurfaceFinish, MaterialRemovalType } from '@frontend/api';
import { RUNES_CALLOUT_TYPES, MATERIAL_REMOVAL_TYPES } from '@frontend/api';
import {
  FormControl,
  FormLabel,
  FormErrorMessage,
  Select,
  Checkbox,
  Flex,
  GDTSymbolText,
  Input,
} from '@frontend/components';

import { SubFormProps } from '../types';
import { FormPreviewSection } from './shared/FormPreviewSection';
import { NumberValueField } from './shared/NumberInputValue';
import { SubFormWrapper } from './shared/SubFormWrapper';
import {
  createRequiredSelectField,
  createBooleanField,
} from './shared/validation';

type SurfaceFinishFormValues = {
  materialRemovalType: MaterialRemovalType;
  hasRaMean: boolean;
  raMean?: number;
  hasRaRange: boolean;
  raRangeMin?: number;
  raRangeMax?: number;
  raClass: string;
};

const initialValues: SurfaceFinishFormValues = {
  materialRemovalType: MATERIAL_REMOVAL_TYPES.UNSPECIFIED,
  hasRaMean: false,
  raMean: undefined,
  hasRaRange: false,
  raRangeMin: undefined,
  raRangeMax: undefined,
  raClass: '',
};

const MATERIAL_REMOVAL_TYPE_OPTIONS = Object.values(MATERIAL_REMOVAL_TYPES).map(
  (value) => ({
    value,
    label: value.charAt(0).toUpperCase() + value.slice(1),
  }),
);

const formatSurfaceFinishSummary = (
  values: SurfaceFinishFormValues,
): string => {
  const symbol =
    values.hasRaMean && values.raMean !== undefined ? `${values.raMean}√` : '√';

  const parts = [symbol];

  if (
    values.hasRaRange &&
    values.raRangeMin !== undefined &&
    values.raRangeMax !== undefined
  ) {
    parts.push(`[${values.raRangeMin}–${values.raRangeMax}]`);
  }
  if (values.raClass) {
    parts.push(values.raClass);
  }

  return parts.join(' ');
};

const validationSchema = object().shape({
  materialRemovalType: createRequiredSelectField('Material removal type'),
  hasRaMean: createBooleanField(),
  raMean: number()
    .typeError('Ra Mean must be a number')
    .min(0, 'Ra Mean must be non-negative')
    .when('hasRaMean', {
      is: true,
      then: (schema) => schema.required('Ra Mean is required when enabled'),
      otherwise: (schema) => schema.notRequired(),
    }),
  hasRaRange: createBooleanField(),
  raRangeMin: number()
    .typeError('Min roughness must be a number')
    .min(0, 'Min roughness must be non-negative')
    .when('hasRaRange', {
      is: true,
      then: (schema) =>
        schema.required('Min roughness is required when range is enabled'),
      otherwise: (schema) => schema.notRequired(),
    }),
  raRangeMax: number()
    .typeError('Max roughness must be a number')
    .min(0, 'Max roughness must be non-negative')
    .when('hasRaRange', {
      is: true,
      then: (schema) =>
        schema
          .required('Max roughness is required when range is enabled')
          .test(
            'greater-than-min',
            'Max must be greater than min',
            function (value) {
              const { raRangeMin } = this.parent;
              if (value === undefined || raRangeMin === undefined) return true;
              return value > raRangeMin;
            },
          ),
      otherwise: (schema) => schema.notRequired(),
    }),
  raClass: string(),
});

export const SurfaceFinishForm = ({
  onSubmit,
  onCancel,
  renderSelectedType,
}: SubFormProps) => {
  const handleSubmit = useCallback(
    (values: SurfaceFinishFormValues) => {
      const surfaceFinish: SurfaceFinish = {
        calloutType: RUNES_CALLOUT_TYPES.SURFACE_FINISH,
        materialRemovalType: values.materialRemovalType,
        raMean:
          values.hasRaMean && values.raMean !== undefined
            ? values.raMean
            : null,
        raRange:
          values.hasRaRange &&
          values.raRangeMin !== undefined &&
          values.raRangeMax !== undefined
            ? [values.raRangeMin, values.raRangeMax]
            : null,
        raClass: values.raClass || null,
      };

      const summary = formatSurfaceFinishSummary(values);
      onSubmit(surfaceFinish, summary);
    },
    [onSubmit],
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnMount
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue, errors, touched, isValid, isSubmitting }) => {
        const selectedOption = MATERIAL_REMOVAL_TYPE_OPTIONS.find(
          (option) => option.value === values.materialRemovalType,
        );

        return (
          <SubFormWrapper
            isValid={isValid}
            isSubmitting={isSubmitting}
            onCancel={onCancel}
            renderSelectedType={renderSelectedType}
            submitLabel="Add Surface Finish"
          >
            <FormPreviewSection>
              <GDTSymbolText>
                {formatSurfaceFinishSummary(values)}
              </GDTSymbolText>
            </FormPreviewSection>

            <Flex direction="column" gap={1} px={1} pb={1}>
              <FormControl>
                <FormLabel>Material Removal Type</FormLabel>
                <Select
                  name="materialRemovalType"
                  options={MATERIAL_REMOVAL_TYPE_OPTIONS}
                  value={selectedOption}
                  onChange={(
                    option: {
                      value: MaterialRemovalType;
                      label: string;
                    } | null,
                  ) => {
                    if (option?.value) {
                      setFieldValue('materialRemovalType', option.value);
                    }
                  }}
                  size="sm"
                  isClearable={false}
                />
                {errors.materialRemovalType && touched.materialRemovalType && (
                  <FormErrorMessage>
                    {errors.materialRemovalType}
                  </FormErrorMessage>
                )}
              </FormControl>

              <Checkbox
                name="hasRaMean"
                isChecked={values.hasRaMean}
                onChange={(e) => setFieldValue('hasRaMean', e.target.checked)}
                isDisabled={values.hasRaRange || !!values.raClass}
                size="sm"
              >
                Include Ra Mean
              </Checkbox>
              {values.hasRaMean && (
                <FormControl>
                  <FormLabel>Ra Mean</FormLabel>
                  <NumberValueField
                    name="raMean"
                    value={values.raMean?.toString() ?? ''}
                    onChange={(value) => {
                      const numValue =
                        value === '' ? undefined : parseFloat(value);
                      setFieldValue(
                        'raMean',
                        isNaN(numValue as number) ? undefined : numValue,
                      );
                    }}
                    size="sm"
                    min={0}
                    allowDecimals={true}
                  />
                  {errors.raMean && touched.raMean && (
                    <FormErrorMessage>{errors.raMean}</FormErrorMessage>
                  )}
                </FormControl>
              )}

              <Checkbox
                name="hasRaRange"
                isChecked={values.hasRaRange}
                onChange={(e) => setFieldValue('hasRaRange', e.target.checked)}
                isDisabled={values.hasRaMean || !!values.raClass}
                size="sm"
              >
                Include Ra Range
              </Checkbox>
              {values.hasRaRange && (
                <>
                  <FormControl>
                    <FormLabel>Min Roughness</FormLabel>
                    <NumberValueField
                      name="raRangeMin"
                      value={values.raRangeMin?.toString() ?? ''}
                      onChange={(value) => {
                        const numValue =
                          value === '' ? undefined : parseFloat(value);
                        setFieldValue(
                          'raRangeMin',
                          isNaN(numValue as number) ? undefined : numValue,
                        );
                      }}
                      size="sm"
                      min={0}
                      allowDecimals={true}
                    />
                    {errors.raRangeMin && touched.raRangeMin && (
                      <FormErrorMessage>{errors.raRangeMin}</FormErrorMessage>
                    )}
                  </FormControl>
                  <FormControl>
                    <FormLabel>Max Roughness</FormLabel>
                    <NumberValueField
                      name="raRangeMax"
                      value={values.raRangeMax?.toString() ?? ''}
                      onChange={(value) => {
                        const numValue =
                          value === '' ? undefined : parseFloat(value);
                        setFieldValue(
                          'raRangeMax',
                          isNaN(numValue as number) ? undefined : numValue,
                        );
                      }}
                      size="sm"
                      min={0}
                      allowDecimals={true}
                    />
                    {errors.raRangeMax && touched.raRangeMax && (
                      <FormErrorMessage>{errors.raRangeMax}</FormErrorMessage>
                    )}
                  </FormControl>
                </>
              )}

              <FormControl>
                <FormLabel>Ra Class (optional)</FormLabel>
                <Input
                  name="raClass"
                  value={values.raClass}
                  onChange={(e) => setFieldValue('raClass', e.target.value)}
                  isDisabled={values.hasRaMean || values.hasRaRange}
                  size="sm"
                  placeholder="e.g., N1, N4, N12"
                />
              </FormControl>
            </Flex>
          </SubFormWrapper>
        );
      }}
    </Formik>
  );
};
